<template>
  <div class="post-list-view flex flex-col">
    <page-header
      :title="tr('Posts')"
      :info="tr('Here you can see all posts from your connected Facebook pages and Instagram profiles. Click on any post to view its detailed performance metrics. The list shows your content from all connected sources in one unified view.')"
    />

    <div class="flex flex-col gap-4 py-5">
      <div v-if="hasConnectedSourceProfile" class="flex flex-wrap items-center gap-3">
        <div class="relative min-w-0 flex-1 md:basis-0">
          <search-icon class="pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
          <ui-input
            v-model="searchTerm"
            type="search"
            class="pl-9"
            :placeholder="tr('Search') + '...'"
            :aria-label="tr('Search')"
          />
        </div>
        <ui-responsive-filters
          :active-count="activeFilterCount"
          :title="tr('Filters')"
          :clear-label="tr('Clear filters')"
          :close-label="tr('Close')"
          @clear="() => clearSelectFilters()"
        >
          <template #default="{ stacked }">
            <div :class="stacked ? 'flex flex-col gap-1.5' : 'contents'">
              <ui-label v-if="stacked">{{ tr('Brand') }}</ui-label>
              <brand-filter v-model="brandId" :class="stacked ? 'w-full' : 'min-w-0 flex-1 md:max-w-60'" />
            </div>

            <div :class="stacked ? 'flex flex-col gap-1.5' : 'contents'">
              <ui-label v-if="stacked">{{ tr('Platform') }}</ui-label>
              <ui-select v-model="platformSelectValue">
                <ui-select-trigger :class="stacked ? 'w-full' : 'min-w-0 flex-1 md:max-w-44'" :aria-label="tr('Platform')">
                  <ui-select-value />
                </ui-select-trigger>
                <ui-select-content>
                  <ui-select-item :value="ALL_PLATFORMS">{{ tr('All platforms') }}</ui-select-item>
                  <ui-select-item v-for="option in platformOptions" :key="option.id" :value="option.id">
                    {{ option.name }}
                  </ui-select-item>
                </ui-select-content>
              </ui-select>
            </div>
          </template>
        </ui-responsive-filters>
      </div>

      <template v-if="!isLoading && isListEmpty">
        <empty-screen
          v-if="!isEmptyFilters"
          icon="adjustments"
          :title="tr('No posts match your current filters')"
          :flag-text="tr('Adjust filters')"
          :description="tr('We couldn\'t find any posts that match your selected filters. Try adjusting your filter settings to see more results in your post list.')"
        />
        <empty-screen
          v-else-if="!hasConnectedSourceProfile"
          icon="posts"
          :title="tr('Analyze your Facbook and Instagram posts with AI.')"
          :flag-text="tr('Connect platform')"
          :description="tr('Connect your Facebook and Instagram accounts to see all your posts here. Once connected, our AI will help you analyze your content and create automated advertising campaigns for better performance.')"
          path="/integrations/meta"
        />
        <empty-screen
          v-else
          icon="posts"
          :title="tr('No posts found')"
          :flag-text="tr('Check integrations')"
          :description="tr('There are no available posts from the connected Facebook and Instagram pages.')"
          path="/integrations/meta"
        />
      </template>

      <div
        v-else
        role="table"
        :aria-label="tr('Posts')"
        class="flex flex-col gap-2"
      >
        <div role="row" class="grid items-center gap-x-4 max-md:relative max-md:grid-cols-2 max-md:gap-y-3 md:grid-cols-[12rem_minmax(0,1fr)_5.5rem_7rem_8rem_2rem] px-4 text-xs font-semibold whitespace-nowrap text-muted-foreground max-md:hidden">
          <span role="columnheader">{{ tr('Source') }}</span>
          <span
            v-for="field in availableFields"
            :key="'field-th-' + field.machine_name"
            role="columnheader"
            :aria-sort="ariaSort(field.machine_name)"
          >
            <button
              type="button"
              data-slot="sort-button"
              class="inline-flex cursor-pointer items-center gap-1 hover:text-foreground"
              @click="() => sortBy(field.machine_name)"
            >
              {{ tr(field.label) }}
              <component :is="sortIcon(field.machine_name)" class="size-3.5" />
            </button>
          </span>
          <span role="columnheader" />
        </div>

        <div
          v-for="row in list"
          :key="row.id"
          role="row"
          class="grid items-center gap-x-4 max-md:relative max-md:grid-cols-2 max-md:gap-y-3 md:grid-cols-[12rem_minmax(0,1fr)_5.5rem_7rem_8rem_2rem] glass-tile rounded-xl p-4 md:py-2.5 transition-shadow duration-300 ease-in-out hover:[--tile-glow:color-mix(in_oklab,var(--primary)_40%,transparent)]"
        >
          <div role="cell" class="max-md:col-span-2 max-md:pr-10">
            <social-page :source-profile="row.source_profile" :date="row.created_at" />
          </div>
          <div role="cell" class="min-w-0 max-md:col-span-2">
            <router-link :to="'/posts/' + row.id" class="no-underline">
              <inline-social-content :entity="row" />
            </router-link>
          </div>
          <div role="cell" class="flex flex-col text-sm">
            <span :class="cellLabelClasses">{{ tr('Rating') }}</span>
            <ai-score v-if="row.ai_value" :value="row.ai_value" />
            <loader-circle-icon v-else-if="row.ai_meta_in_progress" class="size-4 animate-spin text-muted-foreground" />
            <template v-else>–</template>
          </div>
          <div role="cell" class="flex flex-col text-sm">
            <span :class="cellLabelClasses">{{ tr('Posted') }}</span>
            <span v-tooltip="formatToYMD(row.created_at) + ', ' + formatTime(row.created_at)" class="w-fit">{{ timeAgo(row.created_at, tr) }}</span>
          </div>
          <div role="cell" class="flex flex-col text-sm tabular-nums">
            <span :class="cellLabelClasses">{{ tr('Expiration date') }}</span>
            <ui-skeleton v-if="row.contentActualityCheckInProgress" class="h-4 w-20" />
            <span v-else-if="row.ai_content_actuality">
              {{ formatToYMD(row.ai_content_actuality) }}
              <span class="text-muted-foreground">{{ formatTime(row.ai_content_actuality) }}</span>
            </span>
            <template v-else>–</template>
          </div>
          <div role="cell" class="flex flex-col items-center gap-1.5 max-md:absolute max-md:top-4 max-md:right-4">
            <arrow-link :to="'/posts/' + row.id" :label="tr('Details')" />
            <ui-button
              v-if="$root.supportHasL2Access"
              variant="outline"
              size="sm"
              @click="() => openCommunicatorDebug(row)"
            >
              <bug-icon />
              <span class="sr-only">{{ tr('Debug') }}</span>
            </ui-button>
          </div>
        </div>

        <template v-if="isLoading || hasMore">
          <div
            v-for="i in (isListEmpty ? [ 1, 2, 3 ] : [ 1, 2 ])"
            :key="'posts-loader-skeleton--' + i"
            ref="scrollPagerTrigger"
            role="row"
            class="grid items-center gap-x-4 max-md:relative max-md:grid-cols-2 max-md:gap-y-3 md:grid-cols-[12rem_minmax(0,1fr)_5.5rem_7rem_8rem_2rem] glass-tile rounded-xl p-4 md:py-2.5"
          >
            <!-- The cells of a loaded row, with placeholders of the same size. -->
            <div role="cell" class="max-md:col-span-2 max-md:pr-10">
              <social-page />
            </div>
            <div role="cell" class="min-w-0 max-md:col-span-2">
              <inline-social-content />
            </div>
            <div role="cell" class="flex flex-col text-sm">
              <span :class="cellLabelClasses">{{ tr('Rating') }}</span>
              <ui-skeleton class="h-6 w-9 rounded-md" />
            </div>
            <div role="cell" class="flex flex-col text-sm">
              <span :class="cellLabelClasses">{{ tr('Posted') }}</span>
              <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-20" /></span>
            </div>
            <div role="cell" class="flex flex-col text-sm">
              <span :class="cellLabelClasses">{{ tr('Expiration date') }}</span>
              <span class="flex h-5 items-center"><ui-skeleton class="h-3.5 w-24" /></span>
            </div>
            <div role="cell" class="flex flex-col items-center gap-1.5 max-md:absolute max-md:top-4 max-md:right-4">
              <ui-skeleton class="size-8 rounded-full" />
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Bug,
  LoaderCircle,
  Search,
} from '@lucide/vue';

import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import AiScore from '@/ui/components/AiScore.vue';
import ArrowLink from '@/ui/components/ArrowLink.vue';
import BrandFilter from '@/ui/components/BrandFilter.vue';
import CommunicatorDebug from '@/ui/components/CommunicatorDebug.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import InlineSocialContent from '@/ui/components/InlineSocialContent.vue';
import PageHeader from '@/ui/components/PageHeader.vue';
import PostModalContent from '@/ui/components/PostModalContent.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import { formatTime, formatToYMD, timeAgo } from '@/utils/date';
import debounce from '@/utils/debounce.js';
import { formatNumber } from '@/utils/number.js';

const ALL_PLATFORMS = 'all';

export default {
  name: 'PostListView',
  components: {
    AiScore,
    ArrowLink,
    BrandFilter,
    BugIcon: Bug,
    EmptyScreen,
    InlineSocialContent,
    LoaderCircleIcon: LoaderCircle,
    SearchIcon: Search,
    SocialPage,
    PageHeader,
  },
  mixins: [ ScrollPagerMixin ],
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (from && from.name && !from.name.startsWith('Posts')) {
        vm.$store.dispatch('posts/resetView', true);
      }
    });
  },
  data() {
    return {
      debouncedloadNewData: debounce(() => this.loadNewData(), 500),
      backPath: null,
      ALL_PLATFORMS,
      cellLabelClasses: 'text-xs font-semibold text-muted-foreground md:hidden',
    };
  },
  computed: {
    isLoading() {
      return this.$store.getters['posts/isLoading'];
    },
    hasMore() {
      return this.$store.getters['posts/hasMore'];
    },
    brandId: {
      get() {
        return this.$store.getters['posts/brandId'];
      },
      set(value) {
        this.$store.commit('posts/setBrandId', value);
      },
    },
    platform: {
      get() {
        return this.$store.getters['posts/platform'];
      },
      set(value) {
        this.$store.commit('posts/setPlatform', value);
      },
    },
    searchTerm: {
      get() {
        return this.$store.getters['posts/searchTerm'];
      },
      set(value) {
        this.$store.commit('posts/setSearchTerm', value);
      },
    },
    orderBy: {
      get() {
        return this.$store.getters['posts/orderBy'];
      },
      set(value) {
        this.$store.commit('posts/setOrderBy', value);
      },
    },
    order: {
      get() {
        return this.$store.getters['posts/order'];
      },
      set(value) {
        this.$store.commit('posts/setOrder', value);
      },
    },
    list() {
      return this.$store.getters['posts/list'];
    },
    isListEmpty() {
      return this.list.length == 0;
    },
    // The select works with strings; the store keeps null for all platforms.
    platformSelectValue: {
      get() {
        return this.platform ?? ALL_PLATFORMS;
      },
      set(value) {
        this.platform = value === ALL_PLATFORMS ? null : value;
      },
    },
    platformOptions() {
      return [
        { id: 'facebook', name: 'Facebook' },
        { id: 'instagram', name: 'Instagram' },
      ];
    },
    hasConnectedSourceProfile() {
      const profiles = this.$root.currentCompany?.source_profiles || [];
      return profiles.length > 0;
    },
    // The filters in the phone filter sheet (the search stays in view).
    activeFilterCount() {
      return [ this.brandId !== null, this.platform !== null ].filter(Boolean).length;
    },
    isEmptyFilters() {
      return this.searchTerm.trim().length === 0
        && this.brandId === null
        && this.platform === null;
    },
    availableFields() {
      const result = [
        { machine_name: 'text', label: 'Content', sortable: true },
        { machine_name: 'ai_value', label: 'Rating', sortable: true },
        { machine_name: 'created_at', label: 'Posted', sortable: true },
        { machine_name: 'ai_content_actuality', label: 'Expiration date', sortable: true },
      ];

      return result;
    },
  },
  watch: {
    searchTerm(val) {
      if (!val || val.trim().length === 0) {
        this.loadNewData();
      }
      else {
        this.debouncedloadNewData();
      }
    },
    brandId() {
      this.loadNewData();
    },
    platform() {
      this.loadNewData();
    },
    '$route.params.id': {
      immediate: true,
      handler(newId) {
        if (!this.$route.path.startsWith('/posts')) {
          return;
        }

        if (newId && !this.$store.getters['posts/postModalOpened']) {
          this.captureBackPath();
          this.openModal();
        }
        else if (!newId) {
          this.$fullscreenModal.closeAll();
        }
      },
    },
    '$fullscreenModal.isVisible'(newVal) {
      if (!newVal) {
        this.$router.replace(this.backPath ?? '/posts');
      }
    },
  },
  beforeUnmount() {
    this.$fullscreenModal.closeAll();
  },
  mounted() {
    if (this.list.length === 0 && this.hasConnectedSourceProfile) {
      this.loadNewData();
    }
  },
  methods: {
    clearSelectFilters() {
      this.brandId = null;
      this.platform = null;
    },
    formatToYMD,
    formatTime,
    formatNumber,
    timeAgo,
    loadNewData() {
      this.$store.dispatch('posts/resetView', true);
      this.loadData();
    },
    loadData() {
      if (this.isLoading || !this.hasMore) {
        return;
      }

      this.$store.dispatch('posts/loadMoreData')
        .catch(err => this.error(err))
        .finally(() => {
          if (this.hasMore) {
            this.$nextTick(() => this.initScrollPager('scrollPagerTrigger', 'loadData'));
          }
        });
    },
    // Same column again flips the direction, a new column starts ascending.
    sortBy(key) {
      this.order = this.orderBy === key && this.order === 'asc' ? 'desc' : 'asc';
      this.orderBy = key;
      this.loadNewData();
    },
    sortIcon(key) {
      if (this.orderBy !== key) {
        return ArrowUpDown;
      }

      return this.order === 'asc' ? ArrowUp : ArrowDown;
    },
    ariaSort(key) {
      if (this.orderBy !== key) {
        return 'none';
      }

      return this.order === 'asc' ? 'ascending' : 'descending';
    },
    linkOrSpan(field) {
      return field.sortable ? 'sort-link' : 'span';
    },
    isConnected(adAccount) {
      return this.selected.includes(adAccount.id);
    },
    captureBackPath() {
      const value = this.$route.query.back;

      this.backPath = typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
        ? value
        : null;

      if (value) {
        this.$router.replace({ path: this.$route.path });
      }
    },
    navigateBack() {
      const previousPath = this.$router.options.history.state?.back;
      if (previousPath === this.backPath) {
        this.$router.back();

        return;
      }

      this.$router.replace(this.backPath);
    },
    openCommunicatorDebug(post) {
      this.$fullscreenModal.open(
        this,
        CommunicatorDebug,
        { entity: post },
        { wide: true },
      );
    },
    openModal() {
      const modal = this.$fullscreenModal.open(
        this,
        PostModalContent,
        {  },
        { closeWithOutclick: false },
      );

      if (modal) {
        modal.$on('closing', () => {
          if (this.backPath && this.$route.path.startsWith('/posts')) {
            this.navigateBack();
          }
        });

        modal.$on('closed', () => {
          if (!this.$route.path.startsWith('/posts')) {
            return;
          }

          if (this.backPath) {
            this.navigateBack();

            return;
          }

          this.$router.push('/posts');
        });
      }
    },
  },
};
</script>
